import { useEffect, useState } from 'react';
import { MessageCircle, Save, Loader2, CheckCircle, AlertCircle } from 'lucide-react';
import AdminLayout from '../components/AdminLayout';
import { apiFetch } from '../lib/api';

interface TemplateDef {
  key: string;
  title: string;
  description: string;
  category: string;
  defaultValue: string;
}

interface LegacyTemplate {
  key: string;
  value: string;
  updatedAt: number;
}

const CATEGORY_ORDER = ['系統通知', '訂單流程', '夥伴與禮品', '食肆優惠', '推廣活動', 'Workflow 旅程'];
const LEGACY_TAB_KEY = '系統設定（唯讀）';

const CATEGORY_DESCRIPTIONS: Record<string, string> = {
  '系統通知': '觸發式通知訊息，例如訂單確認、付款確認。',
  '訂單流程': '與訂單生命週期相關的互動訊息。',
  '夥伴與禮品': '推薦夥伴、辣谷之友、禮品碼相關訊息。',
  '食肆優惠': '顧客發送「香港辣谷優惠 OFFER:xxx」時自動回覆的優惠 Coupon 訊息。',
  '推廣活動': '谷友禮品推廣活動全程訊息：登記確認、後補、寄出通知、分享任務提醒與感謝。',
  'Workflow 旅程': '客戶路徑自動旅程專用模板，可插入旅程步驟中使用。',
};

const TEMPLATES: TemplateDef[] = [
  {
    key: 'whatsapp_order_confirm_buttons',
    title: '訂單確認（按鈕版正文）',
    description: 'Button 訊息的正文（系統自動附「上傳付款證明／查看訂單」按鈕，毋須寫 URL 行）',
    category: '系統通知',
    defaultValue: `收到你的訂單確認！

*訂單編號：{orderNum}*
應付金額： *HK${'{finalPrice}'}*
請盡快完成付款，上傳付款憑證後我們會立即安排 🌶️`,
  },
  {
    key: 'whatsapp_referrer_welcome_buttons',
    title: '推薦人歡迎（按鈕版正文）',
    description: 'Button 訊息正文（系統自動附「複製專屬連結／數據看板／辣腳社群」按鈕）',
    category: '系統通知',
    defaultValue: `歡迎成為 *辣谷夥伴* ！🔥

{name}，你嘅專屬連結、數據看板同辣腳社群——下面一按搞掂 ✨`,
  },
  {
    key: 'whatsapp_friend_welcome_buttons',
    title: '辣谷之友歡迎（按鈕版正文）',
    description: 'Button 訊息正文（系統自動附「複製專屬連結／查看回贈／辣腳社群」按鈕）',
    category: '系統通知',
    defaultValue: `Hi {name}，你已經係 *辣谷之友* ！🔥

朋友經你嘅專屬連結落單，你賺 *10% 現金回贈* ——下面一按搞掂 ✨`,
  },
  {
    key: 'whatsapp_gift_code_buttons',
    title: '禮品碼／專屬優惠組合（按鈕版正文）',
    description: 'Button 訊息正文（系統自動附「複製專屬連結／查看回贈／辣腳社群」按鈕）',
    category: '夥伴與禮品',
    defaultValue: `🎁 {name}，呢個係你嘅專屬優惠組合 ✨

▪ 自己用： *永久 9 折* （10% 自動回贈）
▪ 朋友用：佢 *首單起* 每次落單，你都賺 *10%*

下面一按：複製連結、查看回贈、加入社群 ✨`,
  },
  {
    key: 'whatsapp_offer_already_claimed_buttons',
    title: '優惠重複領取回覆（按鈕版正文）',
    description: 'Button 訊息正文（系統自動附「打開你嘅兌換頁／我的優惠中心」按鈕）',
    category: '食肆優惠',
    defaultValue: `【{restaurantName}】{offerTitle}——你已領取過！

兌換碼： *{redeemCode}*
{validUntilLine}下面按鈕：打開你嘅兌換頁、入優惠中心 ✨
如需重發，請回覆「重發優惠」。`,
  },
  {
    key: 'whatsapp_offer_redeemed_buttons',
    title: '兌換成功感謝（按鈕版正文）',
    description: 'Button 訊息正文（系統自動附「發掘更多優惠／我的優惠中心」按鈕）',
    category: '食肆優惠',
    defaultValue: `✅ 優惠已成功兌換！

【{restaurantName}】{offerTitle}
兌換時間：{redeemedAt}
{reclaimLine}
多謝支持——下面按鈕繼續發掘辣谷好嘢 🌶️`,
  },
  {
    key: 'whatsapp_completed_buttons',
    title: '訂單完成感謝＋夥伴邀約（按鈕版正文）',
    description: 'Button 訊息正文（系統自動附「複製分享連結／查看回贈」按鈕；僅推薦客戶啟用按鈕版）',
    category: '夥伴與禮品',
    defaultValue: `🎉 {name}，訂單 {orderNum} 完成！

多謝支持——你嘅專屬分享連結同回贈看板，下面一按即達 ✨`,
  },
  {
    key: 'whatsapp_admin_message_alert',
    title: '客戶訊息轉發（管理員）',
    description: '每條客戶入站訊息轉發到管理員號碼的格式（只發管理員，客人不會見到；不帶 footer）',
    category: '系統通知',
    defaultValue: `【X1493客戶訊息】{typeLabel}
來電：{from}
時間：{time}
內容：{content}{orderLinkLine}

請到後台「WhatsApp 對話」查看並回覆。`,
  },
  {
    key: 'whatsapp_order_confirm',
    title: '訂單確認訊息',
    description: '顧客下單後經 WhatsApp 自動發送',
    category: '系統通知',
    defaultValue: `收到你的訂單確認！

*訂單編號：{orderNum}*

請使用 FPS/Payme 付款 *HK$${'{finalPrice}'}* ，並上傳付款憑證。

訂單付款連結：
{orderLink}

如有問題請直接回覆此訊息 🌶️`,
  },
  {
    key: 'whatsapp_payment_confirmed',
    title: '付款確認通知',
    description: '管理員確認收款後自動發送',
    category: '系統通知',
    defaultValue: `你的訂單 *{orderNum}* 付款已確認，我們會盡快備貨。🌶️`,
  },
  {
    key: 'whatsapp_admin_order_notify',
    title: 'Admin 群組訂單知會',
    description: '任何途徑（KPay 自動／admin 手動／$0 單）確認付款後，獨立發一封到 admin 群組知會。變數：{orderNum} {name} {phone} {amount} {method} {adminLink}',
    category: '系統通知',
    defaultValue: `🔔 訂單付款確認
📋 {orderNum}｜{name}（{phone}）
💰 HK${'{amount}'}｜{method}
🔗 {adminLink}`,
  },
  {
    key: 'whatsapp_order_confirm_rejected',
    title: '落單確認被拒提示（號碼/單號不符）',
    description: '發送者電話與落單登記不符、單號無效或狀態不可確認時回覆的統一模糊安全提示（三種情況同一文案——防枚舉不可區分）。無變數。',
    category: '系統通知',
    defaultValue: `收到你嘅落單確認訊息 ⚠️

為保障交易安全，訂單確認必須由 *落單時登記嘅電話號碼* 發送（如果你有多個 WhatsApp 號碼，請留意用緊邊個發送）。

請核對訂單編號後，改用落單號碼重新發送；或者喺網站輸入訂單編號查詢最新狀態。如有疑問，直接回覆搵我哋。`,
  },
  {
    key: 'whatsapp_completed',
    title: '訂單完成感謝 + 夥伴邀約',
    description: '管理員標記訂單完成後自動發送，並邀請顧客成為推薦夥伴',
    category: '訂單流程',
    defaultValue: `{name}，訂單 *{orderNum}* 已完成送出 🌶️ 食得開心！

順帶一提：朋友經你專屬連結落單，你賺 *10% 回贈*——
👉 {referralLink}
💰 回贈：{dashboardLink}`,
  },
  {
    key: 'whatsapp_referrer_welcome',
    title: '推薦夥伴歡迎訊息',
    description: '顧客發送 X1493回贈 或 香港辣谷 R-XXXXXX 時自動回覆',
    category: '夥伴與禮品',
    defaultValue: `歡迎成為 *辣谷夥伴* ！🔥

👉 專屬連結：{referralLink}
📊 數據看板：{dashboardLink}
👥 辣腳社群：{facebookGroup}`,
  },
  {
    key: 'whatsapp_friend_welcome',
    title: '成為辣谷之友歡迎訊息',
    description: '顧客點擊「成為辣谷之友」deeplink 發送 WhatsApp 時自動回覆',
    category: '夥伴與禮品',
    defaultValue: `Hi {name}，你已經係 *辣谷之友* ！🔥

你嘅專屬連結（朋友經 link 落單，你賺 *10% 現金回贈*）：
👉 {referralLink}

💰 回贈睇呢度：{dashboardLink}
👥 辣腳社群：{facebookGroup}`,
  },
  {
    key: 'whatsapp_gift_code',
    title: '禮品碼 / 優惠碼查詢',
    description: '顧客發送 了解禮品碼 / 禮品碼 / 優惠碼 時自動回覆',
    category: '夥伴與禮品',
    defaultValue: `🎁 你嘅 *專屬連結* ：
👉 {referralLink}

▪ 自己用： *永久 9 折* （10% 自動回贈）
▪ 朋友用：佢 *首單起* 每次落單，你都賺 *10%*

💰 回贈：{dashboardLink}
👥 社群：{facebookGroup}`,
  },
  {
    key: 'whatsapp_offer_claimed',
    title: '優惠首次領取訊息',
    description: '顧客首次發送「香港辣谷優惠 OFFER:xxx」成功領取時發送',
    category: '食肆優惠',
    defaultValue: `🎉 領取成功！【{restaurantName}】{offerTitle}

兌換碼： *{redeemCode}*
📱 到店出示兌換頁：{redeemLink}
{validUntilLine}{redeemInstructions}
📦 我的優惠中心（收藏呢條連結）：{myLink}`,
  },
  {
    key: 'whatsapp_offer_already_claimed',
    title: '優惠重複領取提醒',
    description: '顧客再次發送同一優惠時，提醒已有兌換碼',
    category: '食肆優惠',
    defaultValue: `【{restaurantName}】{offerTitle}——你已領取過！

兌換碼： *{redeemCode}*
📱 到店出示兌換頁：{redeemLink}
{validUntilLine}如需重發，請回覆「重發優惠」。
📦 我的優惠中心（收藏呢條連結）：{myLink}`,
  },
  {
    key: 'gf_welcome_claimed',
    title: '禮品登記成功（正選）',
    description: '驗證成功後的履約確認（恭喜＋寄送承諾＋任務詳情＋小約定）。變數：{name} {campaignName} {address} {detailsText}',
    category: '推廣活動',
    defaultValue: `🎁 恭喜你！【{campaignName}】登記成功 ✅

{name} 你好！禮品驗證完成，體驗禮遇正式生效：

📮 禮品將於 3-5 個工作天內郵寄到：
{address}

━━━ 📋 分享任務詳情 ━━━
{detailsText}

📸 小約定：收到禮品後 7 日內出分享帖（tag #香港辣谷），後續谷友福利繼續有你份 🌶️

有任何問題隨時聯繫我哋 🌶️`,
  },
  {
    key: 'gf_welcome_waitlist',
    title: '禮品後補登記確認',
    description: '名額已滿時的後補確認（仍即時成為辣谷之友＋專屬分享連結）。變數：{name} {campaignName} {address} {referralLink} {detailsText}',
    category: '推廣活動',
    defaultValue: `✅ 登記成功！【{campaignName}】後補確認

{name} 你好！正選名額暫時已滿，你已列入後補——一有名額即刻 WhatsApp 通知你 📲

同時你已即時成為辣谷之友 ✅ 你嘅專屬分享連結（朋友經你落單，你有回贈）：
{referralLink}

⏳ 禮品會喺有正選名額後寄出：
{address}

有任何問題隨時聯繫我哋 🌶️`,
  },
  {
    key: 'gf_link_reminder',
    title: '分享連結輕提醒（舊會員）',
    description: '已有辣谷之友完成驗證後，延遲 15-20 秒補發的會員連結提醒。變數：{referralLink}',
    category: '推廣活動',
    defaultValue: `👉 順便提你：你嘅會員分享連結（朋友落單你有回贈）：
{referralLink}`,
  },
  {
    key: 'gf_sent_notice',
    title: '禮品寄出通知',
    description: '後台標記「已寄出」時自動發給客人（預期管理＋任務提醒）。',
    category: '推廣活動',
    defaultValue: `📦 禮品已寄出！記得收到後 7 日內出分享帖（詳情睇之前訊息）🌶️ 完成後將分享帖連結貼返嚟就得 ✅`,
  },
  {
    key: 'gf_task_nudge_t2',
    title: '收貨溫馨提醒（寄出後兩日）',
    description: '標記「已寄出」兩日後自動發（軟性邀請分享＋上載按鈕，每人一次；系統自動帶 [📸 提交分享連結] 按鈕）。變數：{name}（worker 每 15 分鐘掃描）',
    category: '推廣活動',
    defaultValue: `📦 {name}，禮品到手未呀？🌶️

希望食得開心！食落覺得正嘅話——影張相、一句真實感想、tag #香港辣谷，喺 FB/IG 分享俾更多辣友 ✨

你嘅一個分享，係對本地品牌最實際嘅支持 🙏

方便嘅時候，撳下面掣貼返連結就得：`,
  },
  {
    key: 'gf_promote',
    title: '後補轉正選通知',
    description: '後補獲得名額時的自動通知。',
    category: '推廣活動',
    defaultValue: `🎉 好消息！你登記嘅谷友禮品已有正選名額——試吃裝即將寄出，記得完成分享任務🌶️`,
  },
  {
    key: 'gf_thanks',
    title: '分享核實感謝',
    description: '管理員核實分享後嘅感謝訊息。變數：{name}',
    category: '推廣活動',
    defaultValue: `🙏 收到你嘅分享！{name} 多謝你為香港辣谷出一分力——後續谷友福利你係優先名單 🌶️`,
  },
  {
    key: 'wanla_welcome',
    title: '辣友卡開通歡迎',
    description: '購買辣友卡付款確認後嘅開通通知。變數：{name} {memberCode} {dashboardLink} {referralLink}',
    category: '推廣活動',
    defaultValue: `🌶️ 辣友卡開通成功！

{name} 你好！你已經係香港辣谷·年費會員 ✅

你的專屬會員碼：*{memberCode}*
即刻起可以去任何參與店鋪免費攞「辣中聖品」一盒 🎁
打開會員中心出示 QR code 俾店員掃就得：
{dashboardLink}

之後每 30 日可以再攞一盒；領足 12 次仲有年度神秘辣禮 🌶️

💰 朋友經你專屬連結落單，你賺 10%：{referralLink}`,
  },
  {
    key: 'wanla_remind',
    title: '辣友卡可領提醒',
    description: '可領日前 3 日開始提醒（09:30-22:00 隨機時段錯峰；同一週期只提醒一次）。變數：{name} {memberCode} {month} {restaurant} {link}（＝會員 dashboard，內有換領 QR code）',
    category: '推廣活動',
    defaultValue: `🌶️ {name}，你嘅辣中聖品等到你喇！

{month} 月換領推薦：{restaurant}
帶住會員碼 *{memberCode}* 去任何參與店鋪免費攞 🎁

打開會員中心出示 QR code 俾店員掃：
{link}`,
  },
  {
    key: 'wanla_renew',
    title: '辣友卡續會邀請',
    description: '會籍到期前 30 日自動發送嘅續會邀請（一個會籍年度一次）。變數：{name} {memberCode} {expireDate} {productLink} {link}',
    category: '推廣活動',
    defaultValue: `🌶️ {name}，你嘅辣友卡會籍就快到期喇！

有效期至：{expireDate}
領足 12 盒年度神秘辣禮等緊你——而家續會仲有早鳥優惠 🎁

續會入口：{productLink}
你的會員碼：*{memberCode}*（換領頁：{link}）`,
  },
  {
    key: 'wanla_monthly',
    title: '辣友卡月度換領通知（舊制保留）',
    description: 'v1 每月一次性換領碼通知——v2 已改冷卻制（改用 wanla_remind），此模板留作歷史記錄備查。變數：{name} {month} {restaurant} {address} {perk} {code} {link} {referralLink}',
    category: '推廣活動',
    defaultValue: `🍜 {month} 月辣中聖品開放換領！

{name} 你好！本月換領餐廳：
🏪 {restaurant}
📍 {address}
🎁 免費換領：{perk}

你的專屬換領碼：*{code}*
到店出示以下連結俾店員核銷就得 👇
{link}

💰 順便：朋友經你專屬連結落單，你賺 10%：{referralLink}

🌶️ 每月一次，下個月再去另一間餐廳！`,
  },
  {
    key: 'whatsapp_new_customer_thankyou',
    title: '新客培育 T+15m：感謝 + 保存建議',
    description: '訂單完成後 15 分鐘發送，教顧客正確保存辣粉',
    category: 'Workflow 旅程',
    defaultValue: `{name}，多謝選擇 *香港辣谷* ！🌶️

💡 產品存陰涼乾爽處，開封後建議冷藏（詳情睇包裝），風味更耐。

歡迎分享食法或打卡，期待好嘢上你餐桌 😊`,
  },
  {
    key: 'whatsapp_new_customer_recipe',
    title: '新客培育 T+7d：食譜靈感',
    description: '訂單完成後 7 天發送，提供食譜靈感並開啟對話',
    category: 'Workflow 旅程',
    defaultValue: `{name}，好嘢食得點？😋

無論撈麵、炒餸、蘸醬定醃製，辣味好嘢樣樣皆宜。

試到新配搭歡迎分享，我哋會推介畀更多辣友 🔥`,
  },
  {
    key: 'whatsapp_new_customer_restock',
    title: '新客培育 T+21d：補貨鋪排',
    description: '訂單完成後 21 天發送，輕度提醒補貨並提供落單連結',
    category: 'Workflow 旅程',
    defaultValue: `{name}，屋企辣貨庫存還好嗎？🔥

快食完可以提早補貨，我哋幫你預留 *最新鮮嘅貨* 。

直接落單：
{orderLink}`,
  },
  {
    key: 'whatsapp_new_customer_repurchase',
    title: '新客培育 T+28d：回購 + 夥伴邀約',
    description: '訂單完成後 28 天發送，提醒回購並邀請成為推薦夥伴',
    category: 'Workflow 旅程',
    defaultValue: `{name}，辣貨庫存還好嗎？😊

補貨 👉 {orderLink}

順帶一提：朋友經你連結落單，你賺 *10%*——
{referralLink}`,
  },
  {
    key: 'whatsapp_abandoned_payment_help',
    title: '棄單挽回：疑難協助',
    description: 'pending_payment 訂單於指定時間後主動詢問是否遇到付款疑難',
    category: 'Workflow 旅程',
    defaultValue: `Hi {name}，見到你仲未完成付款 （ *{orderNum}* ） 。

係咪付款流程上有咩疑難？或者想問多啲先？直接回覆我哋，專人幫你解決 🌶️

如果你想繼續，呢度可以即刻完成：
{orderLink}`,
  },
  {
    key: 'whatsapp_referred_friend_welcome',
    title: '被推薦新客到達獎勵',
    description: '經推薦人介紹的新客首次下單時發送，贈送隨單禮品',
    category: 'Workflow 旅程',
    defaultValue: `歡迎成為 *辣谷之友* ！🌶️

多謝一位朋友介紹你認識 *香港辣谷* 。為表謝意，我哋已為你準備咗一份小禮物 *{giftName}* ，會隨你今次訂單一齊送出。

收到後歡迎分享食後感，有任何問題隨時聯繫我哋！`,
  },
  {
    key: 'whatsapp_referrer_milestone_referrals',
    title: '推薦人里程碑：推薦單數達標',
    description: '推薦人達成 5/10/25 位朋友推薦時發送祝賀',
    category: 'Workflow 旅程',
    defaultValue: `🎉 第 *{referralCount}* 位朋友經你認識辣谷！

小禮物 *{giftName}* 隨你下次訂單送出 🎁
👉 繼續分享：{referralLink}`,
  },
  {
    key: 'whatsapp_referrer_milestone_traffic',
    title: '推薦人里程碑：流量達標',
    description: '推薦人分享連結累積 50/100/500 瀏覽時發送鼓勵',
    category: 'Workflow 旅程',
    defaultValue: `🔥 你條連結越嚟越多人睇！

小禮物 *{giftName}* 隨你下次訂單送出 🎁
👉 繼續分享：{referralLink}`,
  },
  {
    key: 'whatsapp_referrer_activation',
    title: '夥伴激活鼓勵',
    description: '推薦夥伴加入後一段時間仍未開始推薦時自動鼓勵',
    category: 'Workflow 旅程',
    defaultValue: `Hi *辣谷夥伴* ！🔥

朋友經你連結 *首單起*，你就開始賺回贈：
👉 {referralLink}
💰 回贈：{dashboardLink}`,
  },
  {
    key: 'whatsapp_commission_notification',
    title: '佣金入帳通知',
    description: '推薦人獲得佣金時自動發送',
    category: 'Workflow 旅程',
    defaultValue: `🎉 你影響了另一位朋友！

訂單金額： *HK$${'{orderAmount}'}*
你獲得回贈： *HK$${'{commission}'}*

詳情：{dashboardLink}

多謝把 *辣嘅快樂* 分享出去 🌶️`,
  },
  {
    key: 'whatsapp_offer_redeemed',
    title: '優惠已兌換通知',
    description: '顧客 QR 核銷成功後自動發送',
    category: '食肆優惠',
    defaultValue: `🎁 優惠已成功兌換！

{restaurantName}「{offerTitle}」
兌換碼：{redeemCode}
兌換時間：{redeemedAt}

多謝你使用香港辣谷優惠，下次再嚟！🌶️`,
  },
  {
    key: 'whatsapp_offer_redeem_reminder',
    title: '優惠未兌換提醒（D7/D14）',
    description: '領取後 7／14 天仍未使用，飯點時段自動提醒',
    category: '食肆優惠',
    defaultValue: `【香港辣谷】你仲未用嘅優惠 🎁

餐廳：{restaurantName}
優惠：{offerTitle}
到期日：{validUntil}

📱 出示此頁面即可兌換：{redeemLink}
（到店俾店員掃 QR Code 就得）`,
  },
  {
    key: 'whatsapp_merchant_otp',
    title: '商家中心 OTP 驗證碼',
    description: '商家以電話登入商家中心時發送',
    category: '系統通知',
    defaultValue: `【香港辣谷 商家中心】

你嘅登入驗證碼：*{code}*

驗證碼 10 分鐘內有效。如非本人操作，請忽略此訊息。`,
  },
  {
    key: 'whatsapp_merchant_approved',
    title: '商家優惠批准＋自動開戶通知',
    description: '批准商家提交的優惠時發送（含優惠連結＋商家中心連結）',
    category: '系統通知',
    defaultValue: `🎉 {restaurantName}，優惠已上架！
👉 {offerLink}

商家中心同時開通（睇數據／下載QR／改核銷碼）：
👉 {merchantLink}
📖 使用商家中心即代表同意《商家服務協議》：https://hkspicyvalley.com/merchant/agreement`,
  },
  {
    key: 'whatsapp_merchant_link',
    title: '商家中心連結發放通知',
    description: 'Admin 發放／輪換長期連結時自動發送給商家',
    category: '系統通知',
    defaultValue: `【香港🌶️辣谷 商家中心】

{merchantName} 你好，你的專屬連結：
👉 {link}

登入可睇優惠實時發放、下載二維碼、分享推廣——建議收藏本訊息。
📖 使用即代表同意《商家服務協議》：https://hkspicyvalley.com/merchant/agreement`,
  },
  {
    key: 'whatsapp_supplier_welcome_buttons',
    title: '供貨商歡迎包（一鍵發送）',
    description: '供貨商管理頁「📲 歡迎包」掣發送；含專屬連結＋手冊＋提交貼士＋新品曝光賣點',
    category: '夥伴與禮品',
    defaultValue: `{supplierName} 你好！歡迎成為香港辣谷供貨夥伴 🌶️

你嘅專屬供貨商連結（長期有效，請收藏做書籤）：
{portalLink}

入門前花 3 分鐘睇埋操作手冊（提交要訣＋常見問題）：
{manualLink}

📌 提交貼士：白底清晰產品圖最易過審；備註寫明批發價＋有效日期，審批更快。提交後 1-2 個工作天會自動 WhatsApp 通知你結果。

🚀 新產品上新＝更大曝光機會：新品上架獲首發推廣位（商店新品推薦＋WhatsApp 推廣訊息）——愈早提交，曝光期愈長！`,
  },
  {
    key: 'whatsapp_supplier_product_approved',
    title: '供貨商產品已上架通知',
    description: '產品提交批准後自動發送給供貨商',
    category: '夥伴與禮品',
    defaultValue: `{supplierName}，你嘅 *{productName}* 已上架！🎉
👉 {productLink}

有新貨隨時再交！🔥`,
  },
  {
    key: 'whatsapp_supplier_product_rejected',
    title: '供貨商產品被拒通知',
    description: '產品提交被拒後自動發送給供貨商（含原因）',
    category: '夥伴與禮品',
    defaultValue: `{supplierName}，*{productName}* 未通過審批。

原因：{reason}

調整後歡迎重交，有疑問直接回覆我哋 🙏`,
  },
  {
    key: 'whatsapp_commission_paid',
    title: '傭金已支付通知（夥伴）',
    description: 'Admin 對銷傭金後自動發送（含付款參考）',
    category: '夥伴與禮品',
    defaultValue: `💸 {name}，佣金已支付！

本次：*{amount}*
累積已收：{totalPaid}
參考：{reference}

多謝同辣谷一齊滾大！🔥`,
  },
  {
    key: 'whatsapp_low_stock_alert',
    title: '低庫存警報（供貨商/管理員）',
    description: '庫存低於警戒值時每日一次自動警報',
    category: 'Workflow 旅程',
    defaultValue: `【香港辣谷 庫存預警】⚠️

產品 *{productName}* 庫存剩低 {stockQuantity}（低於警戒值 {threshold}），記得安排補貨！`,
  },
  {
    key: 'whatsapp_repurchase_nudge',
    title: '回購推動（completed+7天）',
    description: '訂單完成 7 天後自動發送回訪＋回購優惠',
    category: 'Workflow 旅程',
    defaultValue: `【香港辣谷 辣友回訪】🌶️

Hi {name}，早前嘅 *{productName}*（訂單 {orderNum}）食完未？

鍾意嘅話返購有著數：用優惠碼 *{promoCode}* 下單即刻減 {discount}！
👉 {storeLink}`,
  },
];

const PLACEHOLDER_HINT = '可用變數：{orderNum}、{finalPrice}、{paymentLink}、{orderLink}、{name}、{giftCode}、{giftName}、{referralCode}、{referralLink}、{dashboardLink}、{facebookGroup}、{referralCount}、{totalCommission}';

export default function AdminAhJimContent() {
  const [values, setValues] = useState<Record<string, string>>({});
  const [savedValues, setSavedValues] = useState<Record<string, string>>({});
  const [exists, setExists] = useState<Record<string, boolean>>({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [cleaning, setCleaning] = useState(false);
  const [activeCategory, setActiveCategory] = useState<string>(CATEGORY_ORDER[0]);
  const [notice, setNotice] = useState<{ type: 'success' | 'error'; text: string } | null>(null);
  const [legacyTemplates, setLegacyTemplates] = useState<LegacyTemplate[]>([]);

  useEffect(() => {
    if (legacyTemplates.length === 0 && activeCategory === LEGACY_TAB_KEY) {
      setActiveCategory(CATEGORY_ORDER[0]);
    }
  }, [legacyTemplates, activeCategory]);

  useEffect(() => {
    const defaults: Record<string, string> = {};
    TEMPLATES.forEach((t) => (defaults[t.key] = t.defaultValue));
    setValues(defaults);

    const load = async () => {
      setLoading(true);
      const nextValues: Record<string, string> = { ...defaults };
      const nextExists: Record<string, boolean> = {};
      try {
        await Promise.all(
          TEMPLATES.map(async (t) => {
            const res = await apiFetch(`/api/public/admin/cms/${encodeURIComponent(t.key)}`);
            if (res.ok) {
              const json = await res.json();
              const entry = json.data ?? json;
              nextValues[t.key] = entry.value ?? t.defaultValue;
              nextExists[t.key] = true;
            } else {
              nextExists[t.key] = false;
            }
          })
        );
      } catch (e) {
        setNotice({ type: 'error', text: '載入預設回應失敗' });
      }
      setValues(nextValues);
      setSavedValues(nextValues);
      setExists(nextExists);
      setLoading(false);
    };

    const loadLegacy = async () => {
      try {
        const res = await apiFetch('/api/admin/settings/whatsapp-templates');
        if (res.ok) {
          const json = await res.json();
          setLegacyTemplates(json.data?.items ?? []);
        }
      } catch (e) {
        console.warn('Failed to load legacy WhatsApp templates', e);
      }
    };

    load();
    loadLegacy();
  }, []);

  const showNotice = (type: 'success' | 'error', text: string) => {
    setNotice({ type, text });
    setTimeout(() => setNotice(null), 4000);
  };

  const handleSave = async () => {
    setSaving(true);
    setCleaning(true);
    setNotice(null);
    try {
      await Promise.all(
        TEMPLATES.map(async (t) => {
          const value = values[t.key] ?? t.defaultValue;
          if (exists[t.key]) {
            const res = await apiFetch(`/api/public/admin/cms/${encodeURIComponent(t.key)}`, {
              method: 'PUT',
              body: JSON.stringify({ value, isActive: true }),
            });
            if (!res.ok) throw new Error(`更新 ${t.title} 失敗`);
          } else {
            const res = await apiFetch('/api/public/admin/cms', {
              method: 'POST',
              body: JSON.stringify({
                key: t.key,
                type: 'text',
                value,
                label: t.title,
                description: t.description,
                category: t.category,
                isActive: true,
              }),
            });
            if (!res.ok) throw new Error(`新增 ${t.title} 失敗`);
          }
        })
      );
      setExists(Object.fromEntries(TEMPLATES.map((t) => [t.key, true])));
      setSavedValues({ ...values });

      // Clean up duplicate entries in system_settings after successful CMS save
      try {
        const cleanupRes = await apiFetch('/api/admin/settings/whatsapp-templates', { method: 'DELETE' });
        if (!cleanupRes.ok) {
          console.warn('Failed to clean up system_settings whatsapp templates');
        } else {
          setLegacyTemplates([]);
        }
      } catch (e) {
        console.warn('Cleanup request failed', e);
      }

      showNotice('success', '預設回應已儲存，並已清理重複的系統設置記錄');
    } catch (e: any) {
      showNotice('error', e.message || '儲存失敗');
    } finally {
      setSaving(false);
      setCleaning(false);
    }
  };

  const activeTemplates = TEMPLATES.filter((t) => t.category === activeCategory);
  const isLegacyTab = activeCategory === LEGACY_TAB_KEY;

  return (
    <AdminLayout currentPage="default-responses">
      <div className="space-y-6">
        <div>
          <h2 className="text-lg font-bold text-gray-900 dark:text-white mb-1 flex items-center gap-2">
            <MessageCircle className="w-5 h-5 text-red-600" />
            WhatsApp 預設回應
          </h2>
          <p className="text-sm text-gray-500 dark:text-gray-400 mb-4">
            以下訊息會在對應觸發點自動發送給顧客。修改後按「儲存」即可生效。
          </p>

          {notice && (
            <div
              className={`mb-4 p-3 rounded-lg text-sm flex items-center gap-2 ${
                notice.type === 'success'
                  ? 'bg-green-50 text-green-700 border border-green-200 dark:bg-green-900/20 dark:text-green-300 dark:border-green-800'
                  : 'bg-red-50 text-red-700 border border-red-200 dark:bg-red-900/20 dark:text-red-300 dark:border-red-800'
              }`}
            >
              {notice.type === 'success' ? <CheckCircle size={16} /> : <AlertCircle size={16} />}
              {notice.text}
            </div>
          )}

          {loading ? (
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-8 text-center text-gray-500 dark:text-gray-400">
              <Loader2 size={24} className="animate-spin mx-auto mb-2" />
              加載中...
            </div>
          ) : (
            <>
              <div className="border-b border-gray-200 dark:border-gray-700 mb-6">
                <nav className="flex gap-2 flex-wrap">
                  {CATEGORY_ORDER.map((cat) => (
                    <button
                      key={cat}
                      onClick={() => setActiveCategory(cat)}
                      className={`px-4 py-2.5 text-sm font-medium border-b-2 transition-colors ${
                        activeCategory === cat
                          ? 'border-red-600 text-red-600 dark:text-red-400'
                          : 'border-transparent text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-300'
                      }`}
                    >
                      {cat}
                    </button>
                  ))}
                  {legacyTemplates.length > 0 && (
                    <button
                      key={LEGACY_TAB_KEY}
                      onClick={() => setActiveCategory(LEGACY_TAB_KEY)}
                      className={`px-4 py-2.5 text-sm font-medium border-b-2 transition-colors ${
                        isLegacyTab
                          ? 'border-red-600 text-red-600 dark:text-red-400'
                          : 'border-transparent text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-300'
                      }`}
                    >
                      {LEGACY_TAB_KEY}
                    </button>
                  )}
                </nav>
              </div>

              <div className="mb-4">
                <h3 className="text-base font-bold text-gray-800 dark:text-white">
                  {isLegacyTab ? LEGACY_TAB_KEY : activeCategory}
                </h3>
                <p className="text-xs text-gray-500 dark:text-gray-400">
                  {isLegacyTab
                    ? '以下項目仍保留在 system_settings，已遷移到 CMS 統一管理。點擊「儲存預設回應」後會自動清理。'
                    : CATEGORY_DESCRIPTIONS[activeCategory]}
                </p>
              </div>

              {isLegacyTab ? (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
                  {legacyTemplates.map((t) => (
                    <div
                      key={t.key}
                      className="bg-gray-50 dark:bg-gray-800/50 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-4"
                    >
                      <div className="flex items-center justify-between mb-1">
                        <h3 className="text-sm font-bold text-gray-900 dark:text-white">{t.key}</h3>
                        <span className="text-[10px] px-2 py-0.5 rounded bg-gray-200 text-gray-700 dark:bg-gray-700 dark:text-gray-300">唯讀</span>
                      </div>
                      <textarea
                        value={t.value}
                        readOnly
                        rows={6}
                        className="w-full p-3 border border-gray-300 dark:border-gray-600 rounded-lg text-sm bg-gray-100 dark:bg-gray-900 text-gray-600 dark:text-gray-400 resize-none font-mono"
                      />
                    </div>
                  ))}
                </div>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
                  {activeTemplates.map((t) => (
                    <div
                      key={t.key}
                      className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-4"
                    >
                      <div className="flex items-center justify-between mb-1">
                        <h3 className="text-sm font-bold text-gray-900 dark:text-white">{t.title}</h3>
                        {(values[t.key] ?? t.defaultValue) !== (savedValues[t.key] ?? t.defaultValue) ? (
                          <span className="text-[10px] px-2 py-0.5 rounded bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-300">未儲存</span>
                        ) : (
                          <span className="text-[10px] px-2 py-0.5 rounded bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-300">已儲存</span>
                        )}
                      </div>
                      <p className="text-xs text-gray-500 dark:text-gray-400 mb-3">{t.description}</p>
                      <textarea
                        value={values[t.key] ?? t.defaultValue}
                        onChange={(e) => setValues((prev) => ({ ...prev, [t.key]: e.target.value }))}
                        rows={6}
                        className="w-full p-3 border border-gray-300 dark:border-gray-600 rounded-lg text-sm focus:ring-2 focus:ring-red-500 outline-none bg-white dark:bg-gray-800 dark:text-white resize-none font-mono"
                      />
                      <p className="text-[10px] text-gray-400 mt-1">{PLACEHOLDER_HINT}</p>
                    </div>
                  ))}
                </div>
              )}

              <div className="flex justify-end">
                <button
                  onClick={handleSave}
                  disabled={saving || cleaning}
                  className="inline-flex items-center gap-2 bg-red-600 text-white px-6 py-3 rounded-lg text-sm font-bold hover:bg-red-700 disabled:opacity-60 transition"
                >
                  {saving || cleaning ? <Loader2 size={18} className="animate-spin" /> : <Save size={18} />}
                  {saving ? '儲存中...' : cleaning ? '清理重複項...' : '儲存預設回應'}
                </button>
              </div>
            </>
          )}
        </div>
      </div>
    </AdminLayout>
  );
}
